import { Person } from '../models';
import { myLocalStorage } from '../myLocalStorage';

export const GA_EVENT_CHALLENGE_TROUBLE = 'challenge_code_trouble';
export const GA_EVENT_CHALLENGE_TROUBLE_SUCCESS = 'challenge_code_trouble_success';
export const GA_EVENT_CHALLENGE_TROUBLE_ERROR = 'challenge_code_trouble_error';
export const GA_EVENT_SUPPORT_FEEDBACK_SUBMITTED = 'support_feedback_submitted';
export const GA_EVENT_SUPPORT_MENU_CLICKED = 'support_menu_clicked';

const getEmailHash = async (email: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(email);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashString = String.fromCharCode(...hashArray);
  return btoa(hashString);
};

interface TrackEventParams {
  action: string;
  category?: string;
  label?: string;
  [key: string]: any;
}

export const trackEvent = async ({ action, category, label, ...rest }: TrackEventParams) => {
  if (typeof window !== 'undefined') {
    // // eslint-disable-next-line no-console
    // console.log('--- TRACK EVENT ---', { action, category, label, ...rest });

    let finalRest = { ...rest };

    if (category === 'student_reading') {
      try {
        const person: Person | undefined = myLocalStorage.getItem('person');
        if (person?.email) {
          const emailHash = await getEmailHash(person.email);
          finalRest = { ...finalRest, user_hash: emailHash };
        }
      } catch (error) {
        //console.log(`Error hashing email for analytics: ${error}`);
      }
    }

    if ((window as any).gtag) {
      (window as any).gtag('event', action, {
        event_category: category,
        event_label: label,
        ...finalRest,
      });
    } else if ((window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: action,
        event_category: category,
        event_label: label,
        ...finalRest,
      });
    }
  }
};

export const GA_EVENT_STUDENT_NAVIGATION = 'student_navigation';
export const GA_EVENT_STUDENT_READING = 'student_reading';
export const GA_EVENT_STUDENT_NAVIGATION_MENU_LANGUAGE_OPENED =
  'student_navigation_menu_language_opened';
export const GA_EVENT_STUDENT_NAVIGATION_MENU_LANGUAGE_SELECTED =
  'student_navigation_menu_language_selected';
export const GA_EVENT_STUDENT_NAVIGATION_MENU_MESSAGES_OPENED =
  'student_navigation_menu_messages_opened';
export const GA_EVENT_STUDENT_NAVIGATION_MENU_PROFILE_OPENED =
  'student_navigation_menu_profile_opened';
export const GA_EVENT_STUDENT_NAVIGATION_READ_OPENED = 'student_navigation_read_opened';
export const GA_EVENT_STUDENT_NAVIGATION_DASHBOARD_AGENDA_OPENED =
  'student_navigation_dashboard_agenda_opened';
export const GA_EVENT_STUDENT_NAVIGATION_DASHBOARD_CALENDAR_OPENED =
  'student_navigation_dashboard_calendar_opened';
export const GA_EVENT_STUDENT_NAVIGATION_CALENDAR_ASSIGNMENT_OPENED =
  'student_navigation_calendar_assignment_opened';
export const GA_EVENT_STUDENT_NAVIGATION_ASSIGNMENTS_OPENED =
  'student_navigation_assignments_opened';
export const GA_EVENT_STUDENT_NAVIGATION_DASHBOARD_ASSIGNMENTS_OPENED =
  'student_navigation_dashboard_assignments_opened';
export const GA_EVENT_STUDENT_NAVIGATION_STUDY_GUIDES_OPENED =
  'student_navigation_study_guides_opened';
export const GA_EVENT_STUDENT_NAVIGATION_FLASHCARDS_OPENED = 'student_navigation_flashcards_opened';
export const GA_EVENT_STUDENT_NAVIGATION_FLASHCARDS_FLIPPED =
  'student_navigation_flashcards_flipped';
export const GA_EVENT_STUDENT_NAVIGATION_FLASHCARDS_DECK_CREATED =
  'student_navigation_flashcards_deck_created';
export const GA_EVENT_STUDENT_NAVIGATION_FLASHCARDS_CREATED =
  'student_navigation_flashcards_created';

export const GA_EVENT_STUDENT_FLASHCARD_GAME_OPENED = 'student_flashcard_game_opened';
export const GA_EVENT_STUDENT_FLASHCARD_GAME_STARTED = 'student_flashcard_game_started';
export const GA_EVENT_STUDENT_FLASHCARD_GAME_WON = 'student_flashcard_game_won';
export const GA_EVENT_STUDENT_FLASHCARD_GAME_FAILED = 'student_flashcard_game_failed';

export const GA_EVENT_STUDENT_NAVIGATION_MENU_DASHBOARD_OPENED =
  'student_navigation_menu_dashboard_opened';
export const GA_EVENT_STUDENT_NAVIGATION_MENU_CALENDAR_OPENED =
  'student_navigation_menu_calendar_opened';
export const GA_EVENT_STUDENT_NAVIGATION_MENU_ASSIGNMENTS_OPENED =
  'student_navigation_menu_assignments_opened';
export const GA_EVENT_STUDENT_NAVIGATION_MENU_CONTENT_OPENED =
  'student_navigation_menu_content_opened';
export const GA_EVENT_STUDENT_NAVIGATION_MENU_STUDY_GUIDES_OPENED =
  'student_navigation_menu_study_guides_opened';
export const GA_EVENT_STUDENT_NAVIGATION_MENU_FLASHCARDS_OPENED =
  'student_navigation_menu_flashcards_opened';
export const GA_EVENT_STUDENT_NAVIGATION_MENU_SYLLABUS_OPENED =
  'student_navigation_menu_syllabus_opened';
export const GA_EVENT_STUDENT_NAVIGATION_MENU_CONFIGURATION_OPENED =
  'student_navigation_menu_configuration_opened';

// Discussion editor events (from issues/1047)
export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_BOLD = 'student_discussions_editor_bold';
export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_ITALIC = 'student_discussions_editor_italic';
export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_UNDERLINE = 'student_discussions_editor_underline';
export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_PARAGRAPH_CHANGE =
  'student_discussions_editor_paragraph_change';
export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_ALIGNMENT = 'student_discussions_editor_alignment';
export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_PASTE = 'student_discussions_editor_paste';
export const GA_EVENT_STUDENT_DISCUSSIONS_PREVIEW_CANCEL = 'student_discussions_preview_cancel';
export const GA_EVENT_STUDENT_DISCUSSIONS_PREVIEW_EDIT = 'student_discussions_preview_edit';
export const GA_EVENT_STUDENT_DISCUSSIONS_PREVIEW_BUTTON_CLICK =
  'student_discussions_preview_button_click';

export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_ORDERED_LIST =
  'student_discussions_editor_ordered_list';
export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_BULLETED_LIST =
  'student_discussions_editor_bulleted_list';
export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_LINK = 'student_discussions_editor_link';
export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_INDENT = 'student_discussions_editor_indent';
export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_UNINDENT = 'student_discussions_editor_unindent';
export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_STRIKETHROUGH =
  'student_discussions_editor_strikethrough';
export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_SUBSCRIPT = 'student_discussions_editor_subscript';
export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_SUPERSCRIPT =
  'student_discussions_editor_superscript';
export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_TEXT_COLOR =
  'student_discussions_editor_text_color';
export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_HIGHLIGHT_COLOR =
  'student_discussions_editor_highlight_color';
export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_REMOVE_FORMATTING =
  'student_discussions_editor_remove_formatting';
export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_BLOCKQUOTE =
  'student_discussions_editor_blockquote';
export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_CODE = 'student_discussions_editor_code';
export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_CODE_BLOCK =
  'student_discussions_editor_code_block';
export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_IMAGE = 'student_discussions_editor_image';
export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_TABLE = 'student_discussions_editor_table';
export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_HORIZONTAL_RULE =
  'student_discussions_editor_horizontal_rule';
export const GA_EVENT_STUDENT_DISCUSSIONS_EDITOR_FONT_SIZE = 'student_discussions_editor_font_size';

// Student reading events (from develop)
export const GA_EVENT_STUDENT_READING_MENU_OPENED = 'student_reading_menu_opened';
export const GA_EVENT_STUDENT_READING_TTS_OPENED = 'student_reading_tts_opened';
export const GA_EVENT_STUDENT_READING_TTS_STARTED = 'student_reading_tts_started';
export const GA_EVENT_STUDENT_READING_FONT_SIZE_OPENED = 'student_reading_font_size_opened';
export const GA_EVENT_STUDENT_READING_FONT_SIZE_CHANGED = 'student_reading_font_size_changed';
export const GA_EVENT_STUDENT_READING_SUMMARIES_OPENED = 'student_reading_summaries_opened';
export const GA_EVENT_STUDENT_READING_SEARCH_OPENED = 'student_reading_search_opened';
export const GA_EVENT_STUDENT_READING_NAVIGATION_FORWARD_PRESSED =
  'student_reading_navigation_forward_pressed';
export const GA_EVENT_STUDENT_READING_NAVIGATION_BACKWARD_PRESSED =
  'student_reading_navigation_backward_pressed';
export const GA_EVENT_STUDENT_READING_NAVIGATION_TOC_OPENED =
  'student_reading_navigation_toc_opened';
export const GA_EVENT_STUDENT_READING_NOTE_ADDED = 'student_reading_note_added';
export const GA_EVENT_STUDENT_READING_NOTE_REMOVED = 'student_reading_note_removed';
export const GA_EVENT_STUDENT_READING_LEFT_QUESTION_MARKER_PRESSED =
  'student_reading_left_question_marker_pressed';
export const GA_EVENT_STUDENT_READING_BOTTOM_QUESTION_BUTTON_PRESSED =
  'student_reading_bottom_question_button_pressed';
export const GA_EVENT_STUDENT_READING_FLASHCARDS_OPENED = 'student_reading_flashcards_opened';
export const GA_EVENT_STUDENT_READING_NOTES_AND_HIGHLIGHTS_OPENED =
  'student_reading_notes_and_highlights_opened';
export const GA_EVENT_STUDENT_READING_CITATIONS_AND_COPIED_TEXT_OPENED =
  'student_reading_citations_and_copied_text_opened';
export const GA_EVENT_STUDENT_READING_BOOKMARKS_OPENED = 'student_reading_bookmarks_opened';
export const GA_EVENT_STUDENT_READING_PRINT_OPENED = 'student_reading_print_opened';
export const GA_EVENT_STUDENT_READING_HIGHLIGHT_SELECTED = 'student_reading_highlight_selected';
export const GA_EVENT_STUDENT_READING_HIGHLIGHT_DESELECTED = 'student_reading_highlight_deselected';
export const GA_EVENT_STUDENT_READING_BOOKMARK_SELECTED = 'student_reading_bookmark_selected';
export const GA_EVENT_STUDENT_READING_BOOKMARK_DESELECTED = 'student_reading_bookmark_deselected';
export const GA_EVENT_STUDENT_READING_NAV_PREVIOUS = 'student_reading_nav_previous';
export const GA_EVENT_STUDENT_READING_NAV_NEXT = 'student_reading_nav_next';
export const GA_EVENT_STUDENT_READING_ASSIGNMENT_COMPLETED = 'student_reading_assignment_completed';
